import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadStripe, type Stripe, type StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { humanizeToken } from '../lib/inlineAttribution';
import { Modal } from '../organisms';
import { money } from './primitives';
import { formatDate } from '../lib/orgTimezone';

/**
 * PortalPay (backlog 05.3) — the client-facing Stripe pay surface for the client
 * portal. Wires the REAL backend routes in src/routes/client-portal-pay.js:
 *   • POST /api/client-portal/invoices/:id/pay-intent  → { client_secret,
 *       publishable_key, amount, fee_cents, ... } (Connect + MODE-B fee resolved
 *       server-side; cents live only at the Stripe boundary — dollars here)
 *   • GET  /api/client-portal/invoices/:id/receipt     → the settled receipt
 *
 * The <PaymentElement> confirms the PaymentIntent directly against Stripe test-mode
 * with the server-issued client_secret + publishable_key. The webhook (server-side)
 * reconciles the succeeded intent through recordInvoicePayment; the receipt read
 * reflects it (we poll a few times because the webhook is async).
 *
 * "Pay all" walks the outstanding invoices one real PaymentIntent at a time — each
 * is a genuine per-invoice charge the webhook reconciles independently (a single
 * aggregate intent would break that per-invoice reconciliation, so we don't fake one).
 */

export interface PayableInvoice {
  id: string;
  invoice_number?: string | null;
  amount: number;
  balance: number;
  status: string;
}

interface PayIntentResponse {
  payment_intent_id: string;
  client_secret: string;
  publishable_key: string | null;
  amount: number;
  amount_cents: number;
  currency: string;
  status: string;
  payment_method_types: string[];
  fee_cents: number;
  application_fee_amount: number;
  fee_display_mode: string;
  connected_account: string | null;
}

interface ReceiptResponse {
  invoice: {
    id: string; invoice_number: string | null; status: string;
    amount: number; balance: number; invoice_date: string | null;
    due_date: string | null; last_paid_date: string | null;
  };
  payments: Array<{ id: string; amount: number; payment_date: string | null; method: string | null; status: string | null }>;
  refs: unknown[];
  total_paid: number;
}

// loadStripe must be called once per publishable key (never inside render). Cache
// the promise so re-renders reuse the same Stripe instance.
const stripeCache = new Map<string, Promise<Stripe | null>>();
function stripeFor(pk: string): Promise<Stripe | null> {
  let p = stripeCache.get(pk);
  if (!p) { p = loadStripe(pk); stripeCache.set(pk, p); }
  return p;
}

const dt = (s?: string | null) => formatDate(s);

/** Public trigger: a compact "Pay" button for a single invoice row. */
export function PayInvoiceButton({ invoice, onPaid }: { invoice: PayableInvoice; onPaid: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>Pay</Button>
      {open && (
        <PayModal
          queue={[invoice]}
          onClose={() => setOpen(false)}
          onAllPaid={onPaid}
        />
      )}
    </>
  );
}

/** Public trigger: "Pay all outstanding" across a set of invoices. */
export function PayAllButton({ invoices, onPaid }: { invoices: PayableInvoice[]; onPaid: () => void }) {
  const [open, setOpen] = useState(false);
  if (invoices.length === 0) return null;
  const total = invoices.reduce((s, i) => s + (Number(i.balance) || 0), 0);
  return (
    <>
      <Button size="sm" variant="primary" onClick={() => setOpen(true)}>
        Pay all outstanding ({money(total)})
      </Button>
      {open && (
        <PayModal
          queue={invoices}
          onClose={() => setOpen(false)}
          onAllPaid={onPaid}
        />
      )}
    </>
  );
}

// ── The modal that walks the queue one PaymentIntent at a time ────────────────
function PayModal({ queue, onClose, onAllPaid }: {
  queue: PayableInvoice[]; onClose: () => void; onAllPaid: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [paidCount, setPaidCount] = useState(0);
  const current = queue[index];
  const isLast = index >= queue.length - 1;
  const multi = queue.length > 1;

  function handlePaid() {
    setPaidCount((n) => n + 1);
  }
  function next() {
    if (isLast) { onAllPaid(); onClose(); }
    else setIndex((i) => i + 1);
  }
  function finish() {
    if (paidCount > 0) onAllPaid();
    onClose();
  }

  return (
    <Modal
      open
      onClose={finish}
      size="lg"
      title={multi ? `Pay outstanding invoices (${index + 1} of ${queue.length})` : 'Pay invoice'}
    >
      {current && (
        <InvoicePayStep
          key={current.id}
          invoice={current}
          isLast={isLast}
          onPaid={handlePaid}
          onNext={next}
        />
      )}
    </Modal>
  );
}

// ── One invoice: create intent → render Payment Element → confirm → receipt ───
function InvoicePayStep({ invoice, isLast, onPaid, onNext }: {
  invoice: PayableInvoice; isLast: boolean; onPaid: () => void; onNext: () => void;
}) {
  const [intent, setIntent] = useState<PayIntentResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [succeededIntentId, setSucceededIntentId] = useState<string | null>(null);

  // Create the PaymentIntent for THIS invoice (real server call → real client_secret).
  useEffect(() => {
    let cancelled = false;
    setCreating(true);
    setCreateError(null);
    api.post<PayIntentResponse>(`/api/client-portal/invoices/${invoice.id}/pay-intent`, {})
      .then((res) => { if (!cancelled) setIntent(res); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error: string }).error)
          : 'Could not start the payment. Please try again.';
        setCreateError(msg);
      })
      .finally(() => { if (!cancelled) setCreating(false); });
    return () => { cancelled = true; };
  }, [invoice.id]);

  // A step that can't be paid (intent error, or online pay not enabled) must still
  // offer a way forward, or a 'Pay all' walk dead-ends at the first such invoice and
  // the rest of the queue is unreachable. Skip advances; on the last step it closes.
  const advanceControl = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
      <Button variant="secondary" onClick={onNext}>
        {isLast ? 'Done' : 'Skip to next invoice'}
      </Button>
    </div>
  );

  if (succeededIntentId) {
    return <ReceiptView invoiceId={invoice.id} isLast={isLast} onNext={onNext} />;
  }

  if (creating) {
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-muted, #666)' }}>Preparing secure checkout…</div>;
  }

  if (createError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-status-red, #b00)' }}>{createError}</div>
        {advanceControl}
      </div>
    );
  }

  if (!intent) return null;

  // Online payment requires the firm's Stripe publishable key. Without it the
  // Payment Element cannot mount — say so honestly rather than render a dead form.
  if (!intent.publishable_key) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-muted, #666)', fontSize: 14 }}>
          Online card payment isn’t enabled for your account yet. Please contact your firm
          to settle invoice {invoice.invoice_number || invoice.id.slice(0, 8)} ({money(intent.amount)}).
        </div>
        {advanceControl}
      </div>
    );
  }

  const options: StripeElementsOptions = {
    clientSecret: intent.client_secret,
    appearance: { theme: 'stripe', variables: { colorPrimary: '#14532d' } },
  };

  return (
    <Elements stripe={stripeFor(intent.publishable_key)} options={options}>
      <PaymentForm invoice={invoice} intent={intent} onSuccess={(id) => { setSucceededIntentId(id); onPaid(); }} />
    </Elements>
  );
}

function PaymentForm({ invoice, intent, onSuccess }: {
  invoice: PayableInvoice; intent: PayIntentResponse; onSuccess: (paymentIntentId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    if (err) {
      setError(err.message || 'Payment could not be completed. Please try again.');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onSuccess(paymentIntent.id);
      return;
    }
    setError('Payment did not complete. Please try again.');
    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--color-border, #e2e2df)',
      }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--color-muted, #666)' }}>
            Invoice {invoice.invoice_number || invoice.id.slice(0, 8)}
          </div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{money(intent.amount)}</div>
        </div>
        <Badge variant="info">{intent.currency.toUpperCase()}</Badge>
      </div>

      <PaymentElement onReady={() => setReady(true)} />

      {error && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--color-status-red, #b00)' }}>{error}</div>
      )}

      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" loading={submitting} disabled={!stripe || !ready}>
          {submitting ? 'Processing…' : `Pay ${money(intent.amount)}`}
        </Button>
      </div>
    </form>
  );
}

// ── Receipt: read the real settled receipt (poll while the webhook lands) ──────
function ReceiptView({ invoiceId, isLast, onNext }: { invoiceId: string; isLast: boolean; onNext: () => void }) {
  const q = useQuery({
    queryKey: ['cp', 'receipt', invoiceId],
    queryFn: ({ signal }) => api.get<ReceiptResponse>(`/api/client-portal/invoices/${invoiceId}/receipt`, signal),
    // The webhook settles the invoice asynchronously — refetch until it reflects
    // the payment (status 'paid' or a recorded payment), then stop.
    refetchInterval: (query) => {
      const data = query.state.data as ReceiptResponse | undefined;
      const settled = data && (data.invoice.status === 'paid' || (data.payments && data.payments.length > 0));
      return settled ? false : 1500;
    },
  });

  const data = q.data;
  const settled = !!data && (data.invoice.status === 'paid' || (data.payments && data.payments.length > 0));

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: 'var(--color-status-green, #14532d)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
        }}>✓</div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Payment received</div>
          <div style={{ fontSize: 13, color: 'var(--color-muted, #666)' }}>
            {settled ? 'Your payment has been recorded.' : 'Your payment is being confirmed…'}
          </div>
        </div>
      </div>

      {data && (
        <div style={{
          border: '1px solid var(--color-border, #e2e2df)', borderRadius: 10,
          background: 'var(--color-surface-raised, #fff)', padding: 16, marginBottom: 16,
        }}>
          <ReceiptRow label="Invoice" value={data.invoice.invoice_number || data.invoice.id.slice(0, 8)} />
          <ReceiptRow label="Status" value={<Badge variant={data.invoice.status === 'paid' ? 'success' : 'info'}>{humanizeToken(data.invoice.status || '')}</Badge>} />
          <ReceiptRow label="Invoice amount" value={money(data.invoice.amount)} />
          <ReceiptRow label="Total paid" value={money(data.total_paid)} />
          <ReceiptRow label="Balance" value={money(data.invoice.balance)} />
          {data.invoice.last_paid_date && <ReceiptRow label="Paid on" value={dt(data.invoice.last_paid_date)} />}
          {data.payments.map((p) => (
            <ReceiptRow key={p.id} label={`Payment ${dt(p.payment_date)}`} value={`${money(p.amount)} · ${p.method || 'card'}`} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onNext}>{isLast ? 'Done' : 'Pay next invoice'}</Button>
      </div>
    </div>
  );
}

function ReceiptRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: 14 }}>
      <span style={{ color: 'var(--color-muted, #666)' }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
